/**
 * Lavagem — scan loop simples. Cada leitura dispara um lifecycle scan
 * com target_phase=WASHING; o motor FSM no servidor aceita ou rejeita.
 */
import { useCallback, useState } from 'react';
import { ScanLine, Droplets, CheckCircle2, XCircle } from 'lucide-react';
import { apiPost, ApiError } from '@/shared/api/client';
import { useBarcodeScanner } from '@/shared/scanner/GlobalBarcodeListener';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

interface LogRow { code: string; ok: boolean; message: string; at: number }

export function LavagemPage() {
  const [log, setLog] = useState<LogRow[]>([]);
  const [busy, setBusy] = useState(false);

  const beep = useCallback((ok: boolean) => {
    try {
      const AC = (window as any).AudioContext ?? (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx: AudioContext = new AC();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = ok ? 880 : 220; gain.gain.value = 0.08;
      osc.start(); osc.stop(ctx.currentTime + (ok ? 0.12 : 0.3));
    } catch { /* mute */ }
  }, []);

  const onScan = useCallback(async (code: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await apiPost('/v1/lifecycle/scan', {
        barcode: code, target_phase: 'WASHING',
      }, { idempotencyKey: `wash-${code}-${Date.now()}` });
      setLog((arr) => [{ code, ok: true, message: 'Aceito', at: Date.now() }, ...arr].slice(0, 100));
      beep(true);
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message;
      setLog((arr) => [{ code, ok: false, message: msg, at: Date.now() }, ...arr].slice(0, 100));
      beep(false);
    } finally {
      setBusy(false);
    }
  }, [busy, beep]);

  useBarcodeScanner({ onScan, minLength: 4 });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          Lavagem
        </h1>
        <p className="text-sm text-neutral-500">
          Bipe cada item ou kit ao entrar na termodesinfectora ou ultrassônica. O motor FSM bloqueia incompatíveis.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Escutando scanner" subtitle="Captura HID global" />
          <CardBody className="grid h-48 place-items-center text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="grid h-16 w-16 place-items-center rounded-full bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                <ScanLine size={28} strokeWidth={1.5} />
              </div>
              <p className="text-sm text-neutral-500">Aguardando leitura…</p>
            </div>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Histórico de leituras"
            subtitle={`${log.length} eventos`}
            action={
              <div className="flex items-center gap-2">
                <Badge tone="success">OK: {log.filter((l) => l.ok).length}</Badge>
                <Badge tone="danger">Erro: {log.filter((l) => !l.ok).length}</Badge>
              </div>
            }
          />
          <CardBody className="p-0">
            <ul className="max-h-[28rem] divide-y divide-neutral-200 overflow-y-auto text-sm dark:divide-neutral-700">
              {log.length === 0 ? (
                <li className="grid place-items-center p-10 text-xs text-neutral-500">
                  <Droplets size={28} className="mb-2 text-neutral-400" />
                  Nenhuma leitura ainda.
                </li>
              ) : log.map((l, idx) => (
                <li key={idx} className={`flex items-center gap-3 px-4 py-2 ${l.ok ? '' : 'bg-danger-bg/30'}`}>
                  {l.ok ? <CheckCircle2 size={16} className="text-success" /> : <XCircle size={16} className="text-danger" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-xs">{l.code}</p>
                    <p className={`truncate text-xxs ${l.ok ? 'text-neutral-500' : 'text-danger'}`}>{l.message}</p>
                  </div>
                  <span className="font-mono text-xxs text-neutral-400">{new Date(l.at).toLocaleTimeString()}</span>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
